<template>
  <div>
    <!-- Hero with rotating gallery -->
    <div class="services-hero">
      <div class="hero-gallery" v-if="galleryImages.length">
        <div
          class="hero-gallery-slide"
          v-for="(img, i) in galleryImages"
          :key="img.id || i"
          :class="{ 'active': i === activeSlide }"
        >
          <img :src="img.image_path || img.url || img" :alt="img.alt || 'Selected Cars'" />
        </div>
        <div class="hero-gallery-overlay"></div>
      </div>
      <video v-else autoplay loop muted playsinline class="hero-video-fallback">
        <source src="/frontend/assets/videos/video.mp4" type="video/mp4" />
      </video>

      <!-- Slider arrows -->
      <button
        v-if="galleryImages.length > 1"
        class="hero-arrow hero-arrow--prev"
        @click="prevSlide"
        aria-label="Previous slide"
      >
        <Icon icon="ph:caret-left-bold" class="iconify" />
      </button>
      <button
        v-if="galleryImages.length > 1"
        class="hero-arrow hero-arrow--next"
        @click="nextSlide"
        aria-label="Next slide"
      >
        <Icon icon="ph:caret-right-bold" class="iconify" />
      </button>

      <!-- Text content positioned bottom-left -->
      <div class="hero-content">
        <div class="container">
          <div class="hero-content-inner">
            <div class="paragraph--normal text-uppercase text-color-quinary mb-2">
              <span class="notranslate" translate="no">SELECTED</span> CARS
            </div>
            <h1 class="heading--0 text-uppercase text-color-quinary mb-3">
              <span class="fw-light">OUR</span> SERVICES
            </h1>
            <div class="row">
              <div class="col-lg-5">
                <p class="paragraph--xlarge text-color-senary mb-0">
                  From vehicle acquisition to handover. We handle the entire process so you can focus on choosing the right car.
                </p>
              </div>
            </div>
            <div class="d-lg-none mt-4">
              <a
                href="https://wa.me/421949006016?text=Hello%2C%20I%20am%20interested%20in%20your%20services."
                target="_blank"
                class="btn btn--normal btn--icon--100 btn--icon--100--right btn--primary text-uppercase w-100"
              >
                <Icon icon="ic:baseline-whatsapp" class="iconify" /> GET IN TOUCH
              </a>
            </div>
            <!-- Slide indicators -->
            <div class="hero-indicators" v-if="galleryImages.length > 1">
              <span
                v-for="(img, i) in galleryImages"
                :key="'ind-' + i"
                class="hero-indicator"
                :class="{ 'active': i === activeSlide }"
                @click="goToSlide(i)"
              ></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Services Cards -->
    <div class="section-normal bg-color-tertiary">
      <div class="container">
        <div class="row mb-5">
          <div class="col-lg-6">
            <h2 class="heading--4 fw-light text-color-quinary text-uppercase mb-3">
              WHAT WE DO
            </h2>
            <p class="paragraph--xlarge text-color-senary">
              Every service is built around one goal: getting you behind the wheel of the right car, fast and without complications.
            </p>
          </div>
        </div>

        <div class="row">
          <!-- Vehicle Acquisition -->
          <div class="col-lg-6 mb-4">
            <div class="service-card">
              <div class="service-card--icon">
                <Icon icon="ph:car-profile-bold" class="iconify" />
              </div>
              <div class="service-card--content">
                <h3 class="paragraph--xxxlarge text-color-quinary text-uppercase mb-3">
                  Vehicle Acquisition
                </h3>
                <p class="paragraph--normal text-color-senary mb-4">
                  Access to brand new vehicles in the right specifications. We work with a wide network across Europe to find what you need.
                </p>
                <ul class="service-list">
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>New models, latest production</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>High demand models</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Short lead times</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Verified availability</span>
                  </li>
                </ul>
                <a
                  href="https://wa.me/421949006016?text=Hello%2C%20I%20am%20interested%20in%20vehicle%20acquisition."
                  target="_blank"
                  class="btn btn--small btn--primary text-uppercase mt-3"
                >
                  <Icon icon="ic:baseline-whatsapp" class="iconify" /> Ask about availability
                </a>
              </div>
            </div>
          </div>

          <!-- Available Vehicles -->
          <div class="col-lg-6 mb-4">
            <div class="service-card">
              <div class="service-card--icon">
                <Icon icon="ph:lightning-bold" class="iconify" />
              </div>
              <div class="service-card--content">
                <h3 class="paragraph--xxxlarge text-color-quinary text-uppercase mb-3">
                  Available Vehicles
                </h3>
                <p class="paragraph--normal text-color-senary mb-4">
                  Units ready to be secured without delay. What you see is what we can deliver.
                </p>
                <ul class="service-list">
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Immediate availability</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Fast reservation</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Clear status on every unit</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>No uncertainty</span>
                  </li>
                </ul>
                <router-link
                  to="/inventory"
                  class="btn btn--small btn--primary text-uppercase mt-3"
                >
                  <Icon icon="ph:arrow-right-bold" class="iconify" /> View inventory
                </router-link>
              </div>
            </div>
          </div>

          <!-- Rare Units -->
          <div class="col-lg-6 mb-4">
            <div class="service-card">
              <div class="service-card--icon">
                <Icon icon="ph:diamond-bold" class="iconify" />
              </div>
              <div class="service-card--content">
                <h3 class="paragraph--xxxlarge text-color-quinary text-uppercase mb-3">
                  Rare Units
                </h3>
                <p class="paragraph--normal text-color-senary mb-4">
                  Limited vehicles released in controlled volume. These specifications are not easy to find and move fast.
                </p>
                <ul class="service-list">
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Hard to access specifications</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Low availability</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Time sensitive opportunities</span>
                  </li>
                </ul>
                <a
                  href="https://wa.me/421949006016?text=Hello%2C%20I%20am%20looking%20for%20a%20rare%20specification."
                  target="_blank"
                  class="btn btn--small btn--primary text-uppercase mt-3"
                >
                  <Icon icon="ic:baseline-whatsapp" class="iconify" /> Inquire now
                </a>
              </div>
            </div>
          </div>

          <!-- Vehicle Buyback -->
          <div class="col-lg-6 mb-4">
            <div class="service-card">
              <div class="service-card--icon">
                <Icon icon="ph:handshake-bold" class="iconify" />
              </div>
              <div class="service-card--content">
                <h3 class="paragraph--xxxlarge text-color-quinary text-uppercase mb-3">
                  Vehicle Buyback
                </h3>
                <p class="paragraph--normal text-color-senary mb-4">
                  Looking to sell your car? We buy premium and luxury vehicles directly. Fair valuation, fast decision, no waiting.
                </p>
                <ul class="service-list">
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Quick valuation within 24 hours</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Transparent pricing</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Immediate payment</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>We handle all paperwork</span>
                  </li>
                </ul>
                <a
                  href="https://wa.me/421949006016?text=Hello%2C%20I%20would%20like%20to%20sell%20my%20vehicle."
                  target="_blank"
                  class="btn btn--small btn--primary text-uppercase mt-3"
                >
                  <Icon icon="ic:baseline-whatsapp" class="iconify" /> Get a valuation
                </a>
              </div>
            </div>
          </div>

          <!-- Sell With Us -->
          <div class="col-lg-6 mb-4">
            <div class="service-card">
              <div class="service-card--icon">
                <Icon icon="ph:megaphone-bold" class="iconify" />
              </div>
              <div class="service-card--content">
                <h3 class="paragraph--xxxlarge text-color-quinary text-uppercase mb-3">
                  Sell With Us
                </h3>
                <p class="paragraph--normal text-color-senary mb-4">
                  Let us present your vehicle to the right audience. Professional photos, video content, social media reach and our buyer network do the work for you.
                </p>
                <ul class="service-list">
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Professional photo & video production</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Social media marketing (Instagram, Telegram)</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Listed on our website and partner platforms</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Access to our international buyer network</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Full support until the sale is closed</span>
                  </li>
                </ul>
                <a
                  href="https://wa.me/421949006016?text=Hello%2C%20I%20would%20like%20to%20sell%20my%20car%20with%20you."
                  target="_blank"
                  class="btn btn--small btn--primary text-uppercase mt-3"
                >
                  <Icon icon="ic:baseline-whatsapp" class="iconify" /> Start selling
                </a>
              </div>
            </div>
          </div>

          <!-- Delivery & Collection -->
          <div class="col-lg-6 mb-4 mb-lg-0">
            <div class="service-card">
              <div class="service-card--icon">
                <Icon icon="ph:truck-bold" class="iconify" />
              </div>
              <div class="service-card--content">
                <h3 class="paragraph--xxxlarge text-color-quinary text-uppercase mb-3">
                  Delivery & Collection
                </h3>
                <p class="paragraph--normal text-color-senary mb-4">
                  Defined handover with no friction. Your vehicle arrives prepared, on time, at the location of your choice.
                </p>
                <ul class="service-list">
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Delivery arranged across Europe</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Personal collection available</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Vehicle fully prepared</span>
                  </li>
                  <li>
                    <Icon icon="ph:check-circle-fill" class="iconify" />
                    <span>Direct process, no middlemen</span>
                  </li>
                </ul>
                <a
                  href="https://wa.me/421949006016?text=Hello%2C%20I%20have%20a%20question%20about%20delivery."
                  target="_blank"
                  class="btn btn--small btn--primary text-uppercase mt-3"
                >
                  <Icon icon="ic:baseline-whatsapp" class="iconify" /> Ask about delivery
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Process -->
    <div class="section-normal bg-color-quaternary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase">
          HOW IT WORKS
        </h2>
        <p class="paragraph--xlarge text-color-senary border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          No extra layers. No delays.
        </p>

        <div class="row">
          <div class="col-lg-3 col-md-6 mb-lg-0 mb-4">
            <div class="process-step">
              <div class="process-step--number">01</div>
              <div class="process-step--line"></div>
              <div class="paragraph--xxxlarge text-color-quinary text-uppercase lh-1 mb-2 mt-4">
                Request
              </div>
              <p class="paragraph--normal text-color-senary mb-0">
                Tell us what you are looking for. Model, spec, budget. The more detail, the faster we move.
              </p>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 mb-lg-0 mb-4">
            <div class="process-step">
              <div class="process-step--number">02</div>
              <div class="process-step--line"></div>
              <div class="paragraph--xxxlarge text-color-quinary text-uppercase lh-1 mb-2 mt-4">
                Offer
              </div>
              <p class="paragraph--normal text-color-senary mb-0">
                We come back with real options. Pricing, availability, photos, condition. Everything you need to decide.
              </p>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 mb-lg-0 mb-4">
            <div class="process-step">
              <div class="process-step--number">03</div>
              <div class="process-step--line"></div>
              <div class="paragraph--xxxlarge text-color-quinary text-uppercase lh-1 mb-2 mt-4">
                Confirmation
              </div>
              <p class="paragraph--normal text-color-senary mb-0">
                You confirm, we lock it in. Payment terms, paperwork, logistics. All handled on our side.
              </p>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 mb-lg-0 mb-0">
            <div class="process-step">
              <div class="process-step--number">04</div>
              <div class="process-step--line"></div>
              <div class="paragraph--xxxlarge text-color-quinary text-uppercase lh-1 mb-2 mt-4">
                Delivery
              </div>
              <p class="paragraph--normal text-color-senary mb-0">
                Your vehicle is delivered or ready for collection. Prepared, inspected, and on time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Featured Cars -->
    <FeaturedCars :show-filter="false" />

    <!-- Handover Section (High Value) -->
    <div class="section-normal bg-color-primary handover-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-lg-0 mb-5">
            <div class="handover-image" v-if="handoverImage">
              <img :src="handoverImage" alt="Vehicle Handover" />
            </div>
            <div class="handover-image" v-else>
              <img src="/frontend/assets/images/car_detail.jpeg" alt="Vehicle Handover" />
            </div>
          </div>
          <div class="col-lg-5 offset-lg-1">
            <div class="paragraph--small wide text-uppercase text-color-senary mb-3 letter-spacing">
              PREMIUM HANDOVER
            </div>
            <h2 class="heading--3 text-color-quinary text-uppercase fw-light mb-4">
              EVERY VEHICLE IS<br />HANDED OVER <span class="fw-medium">READY</span>
            </h2>
            <p class="paragraph--xlarge text-color-senary mb-4">
              Every car leaves our facility in prepared condition. We take care of inspection, documentation and final detailing before you take the keys.
            </p>
            <p class="paragraph--xlarge text-color-senary mb-5">
              Delivery to your address or personal collection at our showroom. Your choice, your convenience.
            </p>
            <div class="row">
              <div class="col-6 mb-4">
                <div class="handover-stat">
                  <Icon icon="ph:shield-check-bold" class="iconify text-color-quinary" />
                  <span class="paragraph--normal text-color-quinary">Full inspection</span>
                </div>
              </div>
              <div class="col-6 mb-4">
                <div class="handover-stat">
                  <Icon icon="ph:file-text-bold" class="iconify text-color-quinary" />
                  <span class="paragraph--normal text-color-quinary">All documents ready</span>
                </div>
              </div>
              <div class="col-6">
                <div class="handover-stat">
                  <Icon icon="ph:sparkle-bold" class="iconify text-color-quinary" />
                  <span class="paragraph--normal text-color-quinary">Professional detailing</span>
                </div>
              </div>
              <div class="col-6">
                <div class="handover-stat">
                  <Icon icon="ph:map-pin-bold" class="iconify text-color-quinary" />
                  <span class="paragraph--normal text-color-quinary">Delivery or collection</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="section-normal bg-color-octrary cta-section">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8">
            <h2 class="heading--3 text-color-quinary text-uppercase fw-light mb-4">
              REQUEST A VEHICLE
            </h2>
            <p class="paragraph--xlarge text-color-senary mb-5">
              Send us your requirements and we will respond with available options. No obligations, no pressure.
            </p>
            <div class="d-flex flex-column flex-sm-row gap-3 justify-content-center">
              <a
                href="https://wa.me/421949006016?text=Hello%2C%20I%20would%20like%20to%20request%20a%20vehicle."
                target="_blank"
                class="btn btn--normal btn--secondary text-uppercase px-5"
              >
                <Icon icon="ic:baseline-whatsapp" class="iconify" /> Write us on WhatsApp
              </a>
              <router-link
                to="/car-on-request"
                class="btn btn--normal btn--primary text-uppercase px-5"
              >
                <Icon icon="ph:paper-plane-right-bold" class="iconify" /> Submit a request
              </router-link>
            </div>
            <div class="mt-4">
              <a
                href="tel:+421949006016"
                class="paragraph--normal text-color-senary text-decoration-none"
              >
                <Icon icon="ph:phone-bold" class="iconify" /> +421 949 006 016
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AppMarquee />
  </div>
</template>

<script>
import AppMarquee from '../components/AppMarquee.vue';
import FeaturedCars from '../components/FeaturedCars.vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';

export default {
  components: {
    AppMarquee,
    FeaturedCars,
    Icon
  },
  data() {
    return {
      galleryImages: [],
      activeSlide: 0,
      slideInterval: null,
      handoverImage: null
    };
  },
  mounted() {
    import('../seo.js').then(({ setPageSeo }) => setPageSeo({
      title: 'Car Sourcing Services | Import, Delivery & Buyback | Selected Cars',
      description: 'Full-service European car sourcing: vehicle acquisition from 92 partner dealers, rare and limited edition cars, car buyback program, sell your car with us, and door-to-door enclosed delivery across Europe and worldwide. Factory orders available.',
      keywords: 'car sourcing service, european car import service, car delivery europe, vehicle acquisition, car buyback, sell my luxury car, enclosed car transport, factory order car, rare cars europe, limited edition cars, car import bratislava, door to door car delivery',
      breadcrumbs: [
        { name: 'Home', url: '/' },
        { name: 'Services', url: '/services' }
      ],
      schema: {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Premium Car Sourcing and Import",
        "provider": {
          "@type": "AutoDealer",
          "name": "Selected Cars",
          "url": "https://selected.sk"
        },
        "areaServed": ["Europe", "Middle East", "Asia"],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Car Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "European Car Sourcing", "description": "We source any brand, any model from 92 European partner dealerships" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Vehicle Import & Delivery", "description": "Full import documentation, customs, and enclosed door-to-door delivery" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Car Buyback", "description": "We buy premium vehicles directly - 24h valuation, fair pricing, immediate payment" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Sell With Us", "description": "Professional photography, marketing, and access to international buyer network" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Rare & Limited Cars", "description": "Access to allocation-only models and off-market opportunities" }}
          ]
        }
      }
    }));
    this.fetchGalleryImages();
  },
  beforeUnmount() {
    this.stopSlider();
  },
  methods: {
    async fetchGalleryImages() {
      try {
        const response = await axios.get('/api/gallery');
        const images = response.data || [];
        // Take up to 6 images for the hero slider
        this.galleryImages = images.slice(0, 6);
        // Use the second image as handover image if available
        if (images.length > 1) {
          this.handoverImage = images[1].image_path || images[1].url;
        }
        if (this.galleryImages.length > 1) {
          this.startSlider();
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    },
    startSlider() {
      this.slideInterval = setInterval(() => {
        this.activeSlide = (this.activeSlide + 1) % this.galleryImages.length;
      }, 4000);
    },
    stopSlider() {
      if (this.slideInterval) {
        clearInterval(this.slideInterval);
        this.slideInterval = null;
      }
    },
    goToSlide(index) {
      this.activeSlide = index;
      this.stopSlider();
      this.startSlider();
    },
    prevSlide() {
      this.activeSlide = (this.activeSlide - 1 + this.galleryImages.length) % this.galleryImages.length;
      this.stopSlider();
      this.startSlider();
    },
    nextSlide() {
      this.activeSlide = (this.activeSlide + 1) % this.galleryImages.length;
      this.stopSlider();
      this.startSlider();
    }
  }
};
</script>

<style scoped>
/* Hero Gallery Slider */
.services-hero {
  position: relative;
  overflow: hidden;
  height: 100vh;
  min-height: 600px;
  max-height: 1000px;
  background: #1a1a1d;
}

.hero-gallery {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-gallery-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 1.2s ease;
}

.hero-gallery-slide.active {
  opacity: 1;
}

.hero-gallery-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Gradient: black overlay, stronger at bottom */
.hero-gallery-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.4) 0%,
      rgba(0, 0, 0, 0.2) 40%,
      rgba(0, 0, 0, 0.6) 75%,
      rgba(0, 0, 0, 0.85) 100%
    ),
    linear-gradient(
      to right,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.1) 60%,
      transparent 100%
    );
  z-index: 1;
}

.hero-video-fallback {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Content positioned bottom-left */
.hero-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding-bottom: 4rem;
}

.hero-content-inner {
  max-width: 600px;
}

/* Slider arrows */
.hero-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.hero-arrow:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.hero-arrow .iconify {
  font-size: 1.25rem;
}

.hero-arrow--prev {
  left: 24px;
}

.hero-arrow--next {
  right: 24px;
}

/* Indicators */
.hero-indicators {
  display: flex;
  gap: 8px;
  margin-top: 2rem;
}

.hero-indicator {
  width: 32px;
  height: 3px;
  background: rgba(255, 255, 255, 0.25);
  cursor: pointer;
  transition: all 0.3s ease;
}

.hero-indicator.active {
  background: #fff;
  width: 48px;
}

/* Service Cards */
.service-card {
  background: #2a2a2e;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 2.5rem;
  height: 100%;
  transition: border-color 0.3s ease;
}

.service-card:hover {
  border-color: rgba(255, 255, 255, 0.15);
}

.service-card--icon {
  width: 56px;
  height: 56px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.service-card--icon .iconify {
  font-size: 1.5rem;
  color: #fff;
}

.service-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-list li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.service-list li:last-child {
  border-bottom: none;
}

.service-list li .iconify {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
}

.service-list li span {
  font-size: 0.95rem;
  color: #aeaeae;
}

/* Process Steps */
.process-step {
  position: relative;
}

.process-step--number {
  font-size: 3rem;
  font-weight: 200;
  color: rgba(255, 255, 255, 0.15);
  line-height: 1;
  letter-spacing: -0.02em;
}

.process-step--line {
  width: 40px;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin-top: 1rem;
}

/* Handover Section */
.handover-image {
  position: relative;
  overflow: hidden;
}

.handover-image img {
  width: 100%;
  height: 500px;
  object-fit: cover;
  display: block;
}

.letter-spacing {
  letter-spacing: 0.2em;
}

.handover-stat {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.handover-stat .iconify {
  font-size: 1.25rem;
  opacity: 0.6;
}

/* CTA Section */
.cta-section .btn .iconify {
  font-size: 1.1rem;
}

.cta-section a.paragraph--normal .iconify {
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 991px) {
  .services-hero {
    height: 85vh;
    min-height: 500px;
  }

  .hero-content {
    padding-bottom: 3rem;
  }

  .hero-arrow {
    width: 44px;
    height: 44px;
  }

  .hero-arrow--prev {
    left: 16px;
  }

  .hero-arrow--next {
    right: 16px;
  }

  .handover-image img {
    height: 350px;
  }

  .service-card {
    padding: 2rem;
  }
}

@media (max-width: 767px) {
  .services-hero {
    height: 80vh;
    min-height: 450px;
  }

  .hero-content {
    padding-bottom: 2rem;
  }

  .hero-arrow {
    display: none;
  }

  .hero-indicators {
    margin-top: 1.5rem;
  }

  .handover-image img {
    height: 280px;
  }

  .service-card {
    padding: 1.5rem;
  }

  .service-card--icon {
    width: 48px;
    height: 48px;
  }

  .service-card--icon .iconify {
    font-size: 1.25rem;
  }
}
</style>
